// ============================================================================
// Origination Fee: percent -> dollar conversion, shared by Buyer and
// Refinance (the two calculators whose closing-costs template includes an
// "Origination Factor" row).
//
// THE FIELD: the backend returns it as a percentage of Sales Price
// ("Origination Factor", value_type: "rate", unit: "%", e.g. value: "1"
// meaning 1%). Everywhere it's actually used, it needs to be a dollar
// amount instead: (salesPrice * percent) / 100.
//
// THREE PLACES THIS CONVERSION HAS TO HAPPEN — this is deliberate, not
// duplication to clean up:
//   1. ManualSheetSection's own effect — converts the row in formData as
//      soon as a Sales Price exists, so the field's displayed value is
//      correct while the user is looking at it.
//   2. computeTotals' running total (in each calculator's utils/
//      calculations.js) — a same-render fallback for the case where (1)
//      hasn't run yet (e.g. ManualSheetSection was never mounted this
//      session) or hasn't caught up yet.
//   3. getEffectiveFormData / withOriginationFeeConverted, called right
//      before Save/Download/Print/Email build their payload — the same
//      "ManualSheetSection was never mounted" gap as (2), but for the data
//      that actually gets persisted/exported rather than just what's shown
//      on screen.
// All three used to each duplicate their own copy of the "is this row the
// origination fee" check and the conversion formula, which is what let one
// of them drift out of sync with the other two. Centralized here instead —
// one place to fix if the matching or the formula ever needs to change.
//
// THE isManual FLAG'S SHARP EDGE: every row in this closing-costs array,
// origination or not, can be hand-edited by the user, which sets
// isManual: true so nothing here overwrites their input. But the
// Origination row starts out showing its raw, unconverted percent (before
// any Sales Price exists to convert it) — and a plain blur on that field
// (even just tabbing through it, no actual edit) reformats "1" to "1.00"
// as a matter of course. Treating that reformat as a deliberate override
// would set isManual: true from an incidental blur, which then permanently
// blocks the real conversion above from ever running, regardless of what
// Sales Price gets entered afterward. isDeliberateManualEdit() below is the
// one place that draws this distinction — every onBlur/onChange handler
// that can touch this row should use it instead of unconditionally setting
// isManual: true.
// ============================================================================

// Different backend sections spell this field's key differently, hence
// checking multiple camelKey variants rather than just one.
export function isOriginationRow(item) {
  if (!item) return false;
  if (
    item.camelKey === "originationFactor" ||
    item.camelKey === "originationFactorPct" ||
    item.camelKey === "originationFee"
  ) {
    return true;
  }
  return String(item.key || "").startsWith("origination");
}

function parseNumber(value) {
  return parseFloat(String(value ?? 0).replace(/,/g, "")) || 0;
}

/** (percent * salesPrice) / 100, formatted to 2 decimals as a string. */
export function calcOriginationDollarValue(percent, salesPrice) {
  const autoValue = (parseNumber(salesPrice) * parseNumber(percent)) / 100;
  return autoValue.toFixed(2);
}

/**
 * Percent to use when a row's OWN value/total still holds the raw percent —
 * i.e. before conversion, or as a same-render fallback in a total/display
 * calculation. NOT usable after conversion, since value/total get
 * overwritten with the dollar amount at that point.
 */
export function getOwnRowPercent(item) {
  return parseNumber(item?.value ?? item?.total ?? 0);
}

/**
 * Percent from the loaded template's own origination row — stays correct
 * even after the working row has already been converted to a dollar
 * amount, since the template data is never mutated by that conversion.
 */
export function getTemplateOriginationPercent(conventionalSection) {
  const row = (conventionalSection || []).find(isOriginationRow);
  return parseNumber(row?.value);
}

/**
 * Whether blurring/editing this row right now should count as the user
 * deliberately overriding the calculated value. False only for the
 * Origination row while it's still showing its raw, unconverted percent
 * (camelKey isn't "originationFee" yet) — see the file-level comment above
 * for why that specific case must never set isManual: true.
 */
export function isDeliberateManualEdit(item) {
  return !(isOriginationRow(item) && item.camelKey !== "originationFee");
}

/**
 * Returns a new closingCostsMeta array with the origination-fee row (if
 * any) converted from percent to a dollar amount. Safe to call
 * unconditionally right before building any payload (save/PDF/print/email)
 * — an array with no origination row, a manually-edited row, or a Sales
 * Price of 0 all pass through unchanged.
 *
 * Exists because the conversion primarily happens inside
 * ManualSheetSection's own effect, which only runs while that component is
 * mounted. A user who reaches the Results Modal straight from the address
 * flow (Calculate) without ever opening Manual Sheet would then save/print/
 * email a payload with the raw, unconverted percent still sitting in this
 * row — the on-screen total looks right (computeTotals has its own
 * fallback) but the actual saved/printed/emailed data wouldn't.
 */
export function withOriginationFeeConverted(
  closingCostsMeta,
  conventionalSection,
  salesPrice,
) {
  if (!Array.isArray(closingCostsMeta) || !closingCostsMeta.length) {
    return closingCostsMeta;
  }
  const sp = parseNumber(salesPrice);
  if (!(sp > 0)) return closingCostsMeta;

  let changed = false;
  const updated = closingCostsMeta.map((obj) => {
    if (!isOriginationRow(obj) || obj.isManual) return obj;

    const percent = getTemplateOriginationPercent(conventionalSection);
    const formatted = calcOriginationDollarValue(percent, sp);
    if (
      obj.camelKey === "originationFee" &&
      obj.unit === "$" &&
      obj.value === formatted &&
      obj.total === formatted
    ) {
      return obj;
    }

    changed = true;
    return {
      ...obj,
      camelKey: "originationFee",
      key: "origination_fee",
      label: "Origination Fee",
      unit: "$",
      value: formatted,
      total: formatted,
      isManual: false,
    };
  });

  return changed ? updated : closingCostsMeta;
}
